import { GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, OnDestroy, OnInit, Optional, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { PoBackorder } from "app/model/entities/-po-backorder";

import { DateFns } from "@/shared/date-fns";
import { DialogBaseComponent } from "@/shared/dialog-base.component";
import { EmailService } from "@/shared/email.service";
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox } from "app/shared/ag-fns";
import { AgStyleFns } from "app/shared/ag-style-fns";
import { MenuItem } from "primeng/api";
import { Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EntityEventTypeEnum, ProductType } from "../model/entities/entity-model";
import { DomainBaseComponent, wrapBusy } from "../shared/domain-base.component";
import { DomainService } from "../shared/domain.service";
import { PurchaseManagerComponent } from "./purchase-manager.component";
import { UpdateShipDateDialogComponent } from "./update-ship-date-dialog.component";
import { EmailPromptDialogComponent } from "@/shared/email-prompt-dialog.component";

@Component({
  selector: "app-backorders",
  templateUrl: "./backorders.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BackordersComponent extends DomainBaseComponent implements OnInit, OnDestroy {
  pobGridOptions: GridOptionsExt;
  shouldShowIdColumns = true;

  currentPob: PoBackorder;
  pobItems: PoBackorder[];
  resetGuidMenuItems: MenuItem[];
  currentDate = new Date();
  selectedCount = 0;
  updateDate = new Date();

  private parentSubscriptions: Subscription[] = [];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    private emailService: EmailService,
    @Optional() private purchaseManager: PurchaseManagerComponent
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });

    // Subscribe to parent's button clicks
    if (this.purchaseManager) {
      this.parentSubscriptions.push(
        this.purchaseManager.sendAccountEmailClicked$.subscribe(() => this.sendAccountEmail()),
        this.purchaseManager.sendManufEmailClicked$.subscribe(() => this.sendManufEmail()),
        this.purchaseManager.showHideIdColumnsClicked$.subscribe(() => this.showHideIdColumns()),
        this.purchaseManager.resetAccountGuidClicked$.subscribe(() => this.resetAccountGuid()),
        this.purchaseManager.resetManufGuidClicked$.subscribe(() => this.resetManufGuid()),
        this.purchaseManager.gotoAccountPageClicked$.subscribe(() => this.gotoAccountPage()),
        this.purchaseManager.gotoManufPageClicked$.subscribe(() => this.gotoManufPage()),
        this.purchaseManager.saveChangesClicked$.subscribe(() => this.saveChanges()),
        this.purchaseManager.cancelChangesClicked$.subscribe(() => this.cancelChanges())
      );
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.parentSubscriptions.forEach((sub) => sub.unsubscribe());
    // Hide the buttons when leaving this component
    if (this.purchaseManager) {
      this.purchaseManager.showBackorderButtons = false;
    }
  }

  isApplyDisabled() {
    return <number>this.pobGridOptions.apiX?.getSelectedRows().length === 0;
  }

  async updateFromContext() {
    // this.gridState = AgFns.createGridState(this.route.snapshot.queryParams, 'currentPodItem');

    this.pobGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPobGridReady,
      onRowSelected: this.onRowSelected,
      rowSelection: RowSelectionMultipleCheckbox,
      onFilterChanged: this.onFilterChanged,
      onColumnRowGroupChanged: this.onColumnRowGroupChanged,
      onCellValueChanged: this.onCellValueChanged,
      getRowId: (p) => p.data?.poDetailId.toString(),
      rowModelType: "serverSide",
      // rowGroupPanelShow: "always",
      suppressDragLeaveHidesColumns: true,
      suppressMakeColumnVisibleAfterUnGroup: false,
      stopEditingWhenCellsLoseFocus: true,

      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.currentPob = null;
          this.updateLocation();
        }
      },
    });
    AgFns.captureGridRouteParams(this.pobGridOptions, this.route, "podId");

    this.resetGuidMenuItems = [
      { label: "Reset Account Guid", command: () => this.resetAccountGuid() },
      {
        label: "Reset Manufacturer Guid",
        command: () => this.resetManufGuid(),
      },
    ];

    // await this.requery();
    this.isPageReady = true;

    // Show context-sensitive buttons in parent
    if (this.purchaseManager) {
      setTimeout(() => {
        this.purchaseManager.showBackorderButtons = true;
        this.purchaseManager.backorderButtonsDisabled = !this.currentPob;
      });
    }
  }

  private updateDatasource() {
    const gridApi = this.pobGridOptions.apiX;
    if (gridApi == null) {
      return;
    }
    const uniqSortModel = <ISortModel>[
      { colId: "poDetailId", sort: "asc" },
      { colId: "joDetailId", sort: "asc" },
      { colId: "accountId", sort: "asc" },
      { colId: "productId", sort: "asc" },
      { colId: "orderQty", sort: "asc" },
      { colId: "canceledQty", sort: "asc" },
    ];

    const opts = {
      returnFn: (rows) => {
        rows.forEach((pob) => (pob._isChecked = false));
        this.currentPob = null;
      },
    };
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createQuery(null, "GetPoDetailBackorders"), uniqSortModel, opts);

    gridApi.setGridOption("serverSideDatasource", ds);
  }

  async canDeactivate() {
    this.stopEditing();
    var ok = await super.canDeactivate();
    if (!ok) return false;

    if (this.uow.hasChanges()) {
      const ok = await this.dialogService.yesNoDialog("Changes have not been saved", "Do you want to save before exiting?");
      if (ok) {
        await this.saveChanges();
      }
    }

    this.uow.clearEntities(ProductType);
    return true;
  }

  // This method is called AFTER ngAfterViewInit - this is important
  // because the ColDef's here reference TemplateRef's above that will not be resolved until here.
  onPobGridReady(event: GridReadyEvent) {
    const colDefs = [
      // Not sure what this is for...

      // {
      //   headerName: "Account",
      //   field: "account.accountName",
      //   filter: "agTextColumnFilter",
      //   enableRowGroup: true,
      // },
      {
        headerName: "Manufacturer",
        field: "product.productType.manufacturer.name",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Purchase Order",
        field: "poDetail.poHeader.id",
        filter: "agNumberColumnFilter",
        enableRowGroup: false,
        maxWidth: 160,
      },
      {
        headerName: "SKU",
        field: "product.productType.style",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Features",
        field: "product.featureChoicesExtract",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Description",
        field: "product.productType.description",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Quantities",
        children: [
          {
            headerName: "Order",
            field: "orderQty",
            filter: "agNumberColumnFilter",
            ...AgFns.fixWidth(120),
          },
          {
            headerName: "Open",
            field: "openQty",
            valueGetter: (params) => {
              const data = params.data;
              if (!data) return null;
              return data.orderQty - data.receivedQty - data.canceledQty;
            },
            ...AgFns.fixWidth(120),
          },
        ],
      },
      {
        headerName: "Manufacturer",
        children: [
          // { ...AgFns.createCheckboxProps(" ", "_isChecked"), editable: true, minWidth: 50, maxWidth: 50 },
          {
            headerName: "Exp. Ship Date",
            field: "poDetail.expectedShipDate",
            editable: true,
            cellClass: "cell-editable",
            filter: "agDateColumnFilter",
            cellEditor: "agDateCellEditor",
          },
          {
            headerName: "Exp. Receive Date",
            field: "poDetail.expectedReceiveDate",
          },
          {
            headerName: "Last Updated Timestamp",
            field: "poDetail.expectedShipDateModTs",
            filter: "agDateColumnFilter",
          },
        ],
      },
      {
        headerName: "Job Order",
        filter: true,
        children: [
          {
            headerName: "Account",
            field: "account.accountName",
            filter: "agTextColumnFilter",
            enableRowGroup: true,
          },
          {
            headerName: "Id",
            field: "joDetail.joHeaderId",
            filter: "agNumberColumnFilter",
            ...AgFns.fixWidth(120),
          },
          {
            headerName: "Orig Ship Date",
            field: "joDetail.joHeader.origExpectedShipDate",
            filter: "agDateColumnFilter",
            cellClass: (params) => AgStyleFns.getDueDateCellClass(params.value),
            cellEditor: "agDateCellEditor",
          },
          {
            headerName: "Detail Ship Date",
            field: "joDetail.expectedShipDate",
            editable: true,
            cellClass: (params) => AgStyleFns.getEditableDueDateCellClass(params.value),
            filter: "agDateColumnFilter",
            cellEditor: "agDateCellEditor",
          },
        ],
      },
      // {
      //   headerName: "J/O Ship Date",
      //   field: "joDetail.joHeader.origExpectedShipDate",
      //   filter: "agDateColumnFilter",
      //   cellStyle: (params) => AgStyleFns.getDueDateCellStyle(params.value),
      // },
      // {
      //   headerName: "Detail Ship Date",
      //   field: "joDetail.expectedShipDate",
      //   editable: true,
      //   cellStyle: (params) => {
      //     if (new Date(params.value) < this.currentDate) {
      //       return { "background-color": "#ffcccc" };
      //     }
      //     return { color: "black", backgroundColor: "yellow" };
      //   },
      //   filter: "agDateColumnFilter",
      //   cellEditor: "agDateCellEditor",
      // },
      // {
      //   headerName: "Mft Expected Ship Date",
      //   field: "poDetail.expectedShipDate",
      //   editable: true,
      //   cellStyle: (params) => {
      //     if (
      //       new Date(params.data.poDetail.calcExpectedReceiveDate) <
      //       this.currentDate
      //     ) {
      //       return { "background-color": "#ffcccc" };
      //     }
      //     return { color: "black", backgroundColor: "yellow" };
      //   },
      //   filter: "agDateColumnFilter",
      //   cellEditor: "agDateCellEditor",
      // },
      // {
      //   headerName: "Exp Receive Date",
      //   field: "poDetail.calcExpectedReceiveDate",
      // },
      // {
      //   headerName: "Manuf Last Updated Ts",
      //   field: "poDetail.expectedShipDateModTs",
      //   filter: "agDateColumnFilter",
      // },
      {
        headerName: "Account Id",
        field: "accountId",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Manufacturer Id",
        field: "product.productType.manufacturerId",
        filter: "agNumberColumnFilter",
      },
      // {
      //   headerName: "Purchase Order",
      //   field: "poDetail.poHeaderId",
      //   filter: "agNumberColumnFilter",
      //   cellClass: "ag-left-aligned-cell",
      //   headerClass: "ag-left-aligned-cell",
      //   enableRowGroup: true,
      // },
      {
        headerName: "P/O Detail Id",
        field: "poDetailId",
        filter: "agNumberColumnFilter",
        enableRowGroup: true,
      },
      // { headerName: 'Job Order Detail', field: 'joDetailId', filter: 'agNumberColumnFilter' },
      // { headerName: 'Account Guid', field: 'accountGuid',  },
      // { headerName: 'Manufacturer Guid', field: 'manufacturerGuid',  },
    ];

    const sortModel = [
      { colId: "account.accountName", sort: "asc" },
      { colId: "product.productType.manufacturer.name", sort: "asc" },
      { colId: "product.productType.style", sort: "asc" },
    ] as ISortModel;

    // next line is needed by the buildDatasource method
    AgFns.initGrid(event, colDefs, sortModel);
    this.pobGridOptions.autoGroupColumnDef = {
      sort: "asc",
      minWidth: 300,
    };

    this.showHideIdColumns(false);
    AgFns.autoSizeAllColumns(this.pobGridOptions);

    // This needs to be done last in order to avoid excess query calls
    this.updateDatasource();
    AgFns.applyGridRouteParams(this.pobGridOptions);
    this.pobGridOptions.apiX?.deselectAll();
  }

  async onRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore

    this.selectedCount = <number>this.pobGridOptions.apiX?.getSelectedRows().length;
    if (!e.node.isSelected()) return;

    let pob = e.data;
    if (!pob) {
      // This usually means that we are on a group node so ...
      pob = e.node?.allLeafChildren?.[0]?.data;
      if (!pob) {
        return;
      }
    }
    this.currentPob = pob;

    // Update parent's button disabled state
    if (this.purchaseManager) {
      setTimeout(() => {
        this.purchaseManager.backorderButtonsDisabled = !this.currentPob;
      });
    }

    this.updateLocation(this.currentPob?.poDetailId);
  }
  @wrapBusy
  async onUpdate(event: Event) {
    const r = await DialogBaseComponent.open(UpdateShipDateDialogComponent, this, { updateDate: this.updateDate });
    if (r) {
      this.multiUpdate(r);
    }
    return <boolean>r;
  }

  multiUpdate(updateDate: Date) {
    const selectedNodes = this.pobGridOptions.apiX?.getSelectedNodes();
    if (!selectedNodes || !selectedNodes.length) {
      return;
    }

    const poBackorders = selectedNodes.map((x) => x.data as PoBackorder).filter((x) => !!x) as any[];
    poBackorders.forEach((bo) => {
      bo.poDetail.expectedShipDate = updateDate;
      bo.poDetail.expectedReceiveDate = DateFns.dateAdd(
        bo.poDetail.expectedShipDate,
        bo.poDetail.poHeader.manufacturer.transitTimeDays,
        "days"
      );
      if (bo.poDetail.joDetail && bo.poDetail.joDetail.expectedShipDate != bo.poDetail.expectedReceiveDate) {
        bo.poDetail.joDetail.expectedShipDate = bo.poDetail.expectedReceiveDate;
        this.dbSaveService.createEntityEvent({
          entityTypeName: "JoDetail",
          entityId: bo.poDetail.joDetail.id.toString(),
          entityEventTypeId: EntityEventTypeEnum.JoDetailExpectedShipDateChange,
        });
      }
      bo.poDetail.expectedShipDateModTs = new Date(Date.now());
    });
    this.pobGridOptions.apiX.refreshCells();
    this.updateParentDirtyState();
  }

  async onCellValueChanged(event) {
    const colId = event?.column?.colId;
    if (colId != "poDetail.expectedShipDate") return;
    const poDetail = this.currentPob.poDetail;
    poDetail.expectedReceiveDate = DateFns.dateAdd(poDetail.expectedShipDate, poDetail.poHeader.manufacturer.transitTimeDays, "days");
    if (poDetail.joDetail && poDetail.joDetail.expectedShipDate != poDetail.expectedReceiveDate) {
      poDetail.joDetail.expectedShipDate = poDetail.expectedReceiveDate;
      this.dbSaveService.createEntityEvent({
        entityTypeName: "JoDetail",
        entityId: poDetail.joDetail.id.toString(),
        entityEventTypeId: EntityEventTypeEnum.JoDetailExpectedShipDateChange,
      });
    }
    poDetail.expectedShipDateModTs = new Date(Date.now());
    this.updateParentDirtyState();
  }

  onFilterChanged() {
    this.updateLocation();
  }

  onColumnRowGroupChanged(event) {
    // hides columns when grouped
    // it turns out we don't need to unhide them when ungrouped
    // because this happens automatically
    const cdefs = this.pobGridOptions.columnDefs;
    const gridApi = this.pobGridOptions.apiX;
    const groupColumns = gridApi.getRowGroupColumns();
    const groupColumnNames = groupColumns.map((gc) => gc.getColId());
    gridApi.setColumnsVisible(groupColumnNames, false);
    this.updateLocation();
  }

  async saveChanges() {
    const sr = await this.dbSaveService.saveChanges();
    this.pobGridOptions?.apiX?.refreshCells();
    this.toastr.success(`Manufacturer Ship Dates and Order Detail Ship Dates Successfully Updated`, "Datebase Action");
    this.updateParentDirtyState();
  }

  async cancelChanges() {
    this.dbSaveService.rejectChanges();
    this.pobGridOptions.apiX.refreshCells();
    this.updateParentDirtyState();
  }

  private updateParentDirtyState() {
    if (this.purchaseManager) {
      setTimeout(() => {
        this.purchaseManager.backorderFormDirty = this.uow.hasChanges();
      });
    }
  }

  showHideIdColumns(forceShowHide: boolean = null) {
    if (forceShowHide == null) {
      this.shouldShowIdColumns = !this.shouldShowIdColumns;
    } else {
      this.shouldShowIdColumns = forceShowHide;
    }
    const gridApi = this.pobGridOptions.apiX;
    if (gridApi) {
      gridApi.setColumnsVisible(
        ["accountId", "product.productType.manufacturerId", "poDetail.poHeaderId", "poDetailId", "joDetailId"],
        this.shouldShowIdColumns
      );
    }
  }

  updateLocation(key: any = null) {
    const urlTree = this.router.createUrlTree(["/purchasing/manage/backorders"]);
    const url = AgFns.buildGridRouteParamsUrl(urlTree, this.pobGridOptions, key && key.toString());
    this.domainService.location.replaceState(url);
  }

  stopEditing() {
    this.pobGridOptions.apiX?.stopEditing();
  }

  getTitle() {}

  gotoAccountPage() {
    this.updateLocation(this.currentPob?.poDetailId);
    const accountGuid = this.currentPob?.joDetail.joHeader.account.accountCryptonym.guid;
    if (accountGuid != null) {
      this.router.navigate(["/purchasing/manage/backorders/account", accountGuid]);
    }
  }

  gotoManufPage() {
    this.updateLocation(this.currentPob?.poDetailId);
    const manufGuid = this.currentPob?.product.productType.manufacturer.manufacturerCryptonym.guid;
    if (manufGuid != null) {
      this.router.navigate(["/purchasing/manage/backorders/manufacturer", manufGuid]);
    }
  }

  // JM - 12/26/2026.  Taking this functionality offline since the current structure of the emailed report is not correct for Account Administrators
  async sendAccountEmail() {
    this.toastr.warning("Not currently implemented");
    return;

    const joHeader = this.currentPob?.joDetail.joHeader;
    const account = joHeader.account;
    const accountGuid = account?.accountCryptonym.guid;
    if (accountGuid == null) {
      await this.dialogService.showOkMessage(
        "Unable to locate an Account CryptoGuid",
        "Cannot create this email because there is no Account CryptoGuid to insert into the message"
      );
      return;
    }

    // Prompt for email address
    const emailAddress = await DialogBaseComponent.open(EmailPromptDialogComponent, this, {
      title: "Send Account Backorder Email",
      message: `Enter the email address for ${account.accountName}:`,
      dialogWidth: "450px",
      dialogHeight: "250px",
    });

    // If user cancelled, abort
    if (!emailAddress) {
      return;
    }

    const parsedUrl = new URL(window.location.href);
    const baseUrl = parsedUrl.origin;

    const body = `
Dear Valued Customer,<br/><br/>

We wanted to provide you with an update on items currently on backorder for your account.<br/><br/>

Please click the secure link below to view your current backorder status:<br/><br/>

<a href="${baseUrl}/account-backorders/${accountGuid}">${baseUrl}/account-backorders/${accountGuid}</a><br/><br/>

On this page, you will be able to review:<br/>
&bull; All items currently on backorder<br/>
&bull; Expected shipment dates from manufacturers<br/>
&bull; Order quantities and received quantities<br/><br/>

We are actively working with our suppliers to fulfill your orders as quickly as possible. If you have any questions about specific items or need to make changes to your order, please contact us directly.<br/><br/>

Thank you for your patience and continued business.<br/><br/>

Best regards,<br/>
Unitec Customer Service Team
<br/><br/><hr/>`;

    // Show immediate feedback, then send email asynchronously
    this.toastr.info(`Sending email to ${emailAddress}...`, "Email");
    this.emailService
      .sendBasicEmail(emailAddress, "Unitec Backorder Info", body)
      .then((status) => {
        if (status === "accepted" || status === "delivered" || status === "stored") {
          this.toastr.success(`Email sent successfully to ${emailAddress}`, "Email Sent");
        } else if (status === "rejected" || status === "failed") {
          this.toastr.error(`Email failed: ${status}`, "Email Error");
        } else if (status === "unknown") {
          this.toastr.warning(`Email status unknown - please verify delivery`, "Email Status");
        } else {
          this.toastr.info(`Email status: ${status}`, "Email Status");
        }
      })
      .catch((err) => {
        this.toastr.error(`Failed to send email: ${err.message || err}`, "Email Error");
      });
  }

  async sendManufEmail() {
    const manuf = this.currentPob.product.productType.manufacturer;
    const manufId = manuf.id;
    const manufGuid = manuf.manufacturerCryptonym.guid;
    if (manufGuid == null) {
      await this.dialogService.showOkMessage(
        "Unable to locate an Manufacturer CryptoGuid",
        "Cannot create this email because there is no Manufacturer CryptoGuid to insert into the message"
      );
      return;
    }
    const parsedUrl = new URL(window.location.href);
    const baseUrl = parsedUrl.origin;
    // const emailAddress = manuf?.whEmail || manuf.billingEmail;
    let emailAddress = manuf?.whEmail;
    if (!emailAddress) {
      emailAddress = await DialogBaseComponent.open(EmailPromptDialogComponent, this, {
        title: "Send Manufacturer Backorder Email",
        message: `Enter the email address for ${manuf.name}:`,
        dialogWidth: "450px",
        dialogHeight: "250px",
      });

      // If user cancelled, abort
      if (!emailAddress) {
        return;
      }
    }

    // if (!emailAddress) {
    //   await this.dialogService.showOkMessage("No email address", "No email address is available for this manufacturer.");
    //   return;
    // }

    const body = `
Dear Valued Partner,<br/><br/>

We are reaching out regarding outstanding purchase orders currently on backorder. To ensure accurate delivery timelines for our customers, we kindly request that you update the expected shipment dates for these items.<br/><br/>

Please click the secure link below to access your Uniformax backorder portal:<br/><br/>

<a href="${baseUrl}/manufacturer-backorders/${manufGuid}">${baseUrl}/manufacturer-backorders/${manufGuid}</a><br/><br/>

On the portal, you will be able to:<br/>
&bull; Review all items currently on backorder<br/>
&bull; Enter or update expected shipment dates<br/>
&bull; Submit your updates directly to our system<br/><br/>

Your timely response is greatly appreciated, as it allows us to provide accurate delivery estimates to our customers and plan our inventory accordingly.<br/><br/>

If you have any questions or require assistance, please do not hesitate to contact us.<br/><br/>

Thank you for your continued partnership.<br/><br/>

Best regards,<br/>
Unitec Purchasing Team
<br/><br/><hr/>`;

    // Show immediate feedback, then send email asynchronously
    this.toastr.info(`Sending email to ${emailAddress}...`, "Email");
    this.emailService
      .sendBasicEmail(emailAddress, "Uniformax Backorder Info", body)
      .then((status) => {
        if (status === "accepted" || status === "delivered" || status === "stored") {
          this.toastr.success(`Email sent successfully to ${emailAddress}`, "Email Sent");
        } else if (status === "rejected" || status === "failed") {
          this.toastr.error(`Email failed: ${status}`, "Email Error");
        } else if (status === "unknown") {
          this.toastr.warning(`Email status unknown - please verify delivery`, "Email Status");
        } else {
          this.toastr.info(`Email status: ${status}`, "Email Status");
        }
      })
      .catch((err) => {
        this.toastr.error(`Failed to send email: ${err.message || err}`, "Email Error");
      });
  }

  async resetGuid() {}

  async resetAccountGuid() {
    return await this.dialogService.showOkMessage("Not yet implemented", "This feature has not yet been implemented");
  }

  async resetManufGuid() {
    return await this.dialogService.showOkMessage("Not yet implemented", "This feature has not yet been implemented");
    // const guid = this.UtilFns.generateGUID();
    // const manufId = this.currentPob?.manufacturerId;
    // const manuf = await this.dbQueryService.getManufacturer(manufId);
  }

  goBack() {
    this.domainService.location.back();
  }
}
